import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { collection, addDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Obra, EstadoObra } from '../../types';
import { useObras } from '../../context/ObrasContext';

interface ObraFormProps {
  open: boolean;
  onClose: (wasSuccessful?: boolean) => void;
  obraToEdit: Obra | null;
}

const ESTADOS: EstadoObra[] = ['activa', 'completada', 'suspendida'];

const ObraForm: React.FC<ObraFormProps> = ({ open, onClose, obraToEdit }): React.ReactElement => {
  const { fetchObras } = useObras();
  const [formData, setFormData] = useState<Omit<Obra, 'id'>>({
    nombre: '',
    ubicacion: '',
    estado: 'activa',
    fechaInicio: new Date(),
    fechaLimite: new Date(),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateErrors, setDateErrors] = useState({
    fechaInicio: '',
  });

  useEffect(() => {
    if (obraToEdit) {
      setFormData({
        nombre: obraToEdit.nombre,
        ubicacion: obraToEdit.ubicacion,
        estado: obraToEdit.estado,
        fechaInicio: obraToEdit.fechaInicio,
        fechaLimite: obraToEdit.fechaLimite,
      });
    } else {
      // Resetear el formulario
      setFormData({
        nombre: '',
        ubicacion: '',
        estado: 'activa',
        fechaInicio: new Date(),
        fechaLimite: new Date(),
      });
    }
    setError(null);
    setDateErrors({ fechaInicio: '' });
  }, [obraToEdit, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    try {
      const obraData = {
        ...formData,
        nombre: formData.nombre.trim(),
        ubicacion: formData.ubicacion.trim(),
        fechaInicio: Timestamp.fromDate(formData.fechaInicio),
        fechaLimite: Timestamp.fromDate(formData.fechaLimite),
      };

      if (obraToEdit) {
        await updateDoc(doc(db, 'obras', obraToEdit.id), obraData);
      } else {
        await addDoc(collection(db, 'obras'), obraData);
      }
      await fetchObras();
      onClose(true);
    } catch (error) {
      setError('Error al guardar la obra. Por favor, intente nuevamente.');
      setLoading(false);
    }
  };

  const handleTextChange = (field: 'nombre' | 'ubicacion') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleEstadoChange = (e: SelectChangeEvent<EstadoObra>) => {
    setFormData(prev => ({
      ...prev,
      estado: e.target.value as EstadoObra
    }));
  };

  const handleDateChange = (field: 'fechaInicio') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.value) return; // Ignorar si el valor está vacío

    try {
      const [date, time] = e.target.value.split('T');
      const newDate = new Date(`${date}T${time || '00:00'}`);

      if (isNaN(newDate.getTime())) {
        throw new Error('Fecha inválida');
      }

      setFormData(prev => ({
        ...prev,
        [field]: newDate
      }));

      // Limpiar error cuando el usuario modifica la fecha
      setDateErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    } catch (error) {
      setDateErrors(prev => ({
        ...prev,
        [field]: 'Fecha inválida'
      }));
    }
  };

  const formatDateForInput = (date: Date) => {
    if (!date) return '';
    return date.toISOString().slice(0, 16); // Format: "YYYY-MM-DDTHH:mm"
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '50vh' }
      }}
    >
      <DialogTitle>
        {obraToEdit ? 'Editar Obra' : 'Nueva Obra'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Nombre"
            value={formData.nombre}
            onChange={handleTextChange('nombre')}
            margin="normal"
            required
            autoFocus
            inputProps={{ maxLength: 100 }}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Ubicación"
            value={formData.ubicacion}
            onChange={handleTextChange('ubicacion')}
            margin="normal"
            required
            inputProps={{ maxLength: 200 }}
            disabled={loading}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Estado</InputLabel>
            <Select
              value={formData.estado}
              label="Estado"
              onChange={handleEstadoChange}
              disabled={loading}
            >
              {ESTADOS.map(estado => (
                <MenuItem key={estado} value={estado}>
                  {estado.charAt(0).toUpperCase() + estado.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Fecha y hora de inicio"
              type="datetime-local"
              value={formatDateForInput(formData.fechaInicio)}
              onChange={handleDateChange('fechaInicio')}
              margin="normal"
              error={!!dateErrors.fechaInicio}
              helperText={dateErrors.fechaInicio}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onClose()}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {obraToEdit ? 'Guardar cambios' : 'Crear obra'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ObraForm;